import { useState, useEffect, useRef } from "react";
import api from "../services/api";
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from "../services/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { setupPushNotifications } from '../services/NotificationService';
import AgreementModal from '../components/AgreementModal';

function Login() {
    const isNative = Capacitor.isNativePlatform();

    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [verificationId, setVerificationId] = useState("");       // native path
    const [confirmationResult, setConfirmationResult] = useState(null); // web path
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
const [isResending, setIsResending] = useState(false);

    // ── NEW: brand-new phone number flow ──
    // Once OTP verifies a phone that has no MealBee account yet, the
    // backend tells us via needs_name instead of failing outright. We
    // stash the already-verified Firebase token and show a one-field
    // name screen, then resubmit to finish creating the account.
    const [needsName, setNeedsName] = useState(false);
    const [fullName, setFullName] = useState("");
    const [pendingFirebaseToken, setPendingFirebaseToken] = useState("");
    const [showAgreement, setShowAgreement] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // For roles whose accounts are created by admin (restaurant owner,
    // delivery partner) - no signup screen to gate, so we check on
    // login instead and hold the redirect until they've agreed.
    const [pendingRoleAgreement, setPendingRoleAgreement] = useState(null);

    const recaptchaContainerRef = useRef(null);
    const phoneRef = useRef("");
const otpRequestIdRef = useRef(0);
const otpTimeoutRef = useRef(null);
const loginInProgressRef = useRef(false);

useEffect(() => {
    phoneRef.current = phone;
}, [phone]);
useEffect(() => {
    if (resendTimer <= 0) return;

    const timer = setInterval(() => {
        setResendTimer((previous) => {
            if (previous <= 1) {
                clearInterval(timer);
                return 0;
            }

            return previous - 1;
        });
    }, 1000);

    return () => clearInterval(timer);
}, [resendTimer]);
const clearOtpTimeout = () => {
    if (otpTimeoutRef.current) {
        clearTimeout(otpTimeoutRef.current);
        otpTimeoutRef.current = null;
    }
};
const getFirebaseErrorMessage = (error) => {
    const message =
        typeof error === "string"
            ? error
            : error?.message || "";

    const code = error?.code || "";

    if (
        code.includes("invalid-verification-code") ||
        message.toLowerCase().includes("invalid verification code")
    ) {
        return "Incorrect OTP. Please check the code and try again.";
    }

    if (
        code.includes("code-expired") ||
        message.toLowerCase().includes("code has expired")
    ) {
        return "This OTP has expired. Please request a new OTP.";
    }

    if (
        code.includes("too-many-requests") ||
        message.toLowerCase().includes("too many")
    ) {
        return "Too many OTP attempts. Please wait and try again later.";
    }

    if (
        code.includes("network-request-failed") ||
        message.toLowerCase().includes("network")
    ) {
        return "Network error. Please check your internet connection.";
    }

    if (
        code.includes("invalid-phone-number")
    ) {
        return "Please enter a valid phone number.";
    }

    return message || "Phone verification failed. Please try again.";
};

    // ── Native-only: listen for Firebase phone-auth events ──
    useEffect(() => {
    if (!isNative) return;

    let codeSentListener = null;
    let verificationCompletedListener = null;
    let verificationFailedListener = null;
    let cancelled = false;

    const setupFirebaseListeners = async () => {
        codeSentListener = await FirebaseAuthentication.addListener(
            "phoneCodeSent",
            (event) => {
                if (cancelled) return;

                console.log(
                    "✅ Phone code sent. Verification ID:",
                    event.verificationId
                );

                clearOtpTimeout();

                setVerificationId(event.verificationId);
setOtp("");
setOtpSent(true);
setResendTimer(30);
setIsLoading(false);
setError("");
            }
        );

        verificationCompletedListener =
            await FirebaseAuthentication.addListener(
                "phoneVerificationCompleted",
                async (event) => {
                    if (cancelled) return;

                    console.log(
                        "✅ Automatic phone verification completed:",
                        event
                    );

                    clearOtpTimeout();

                    try {
                        setIsLoading(true);
                        setError("");

                        const tokenResult =
                            await FirebaseAuthentication.getIdToken({
                                forceRefresh: true
                            });

                        if (!tokenResult?.token) {
                            throw new Error(
                                "Firebase ID token was not returned."
                            );
                        }

                        await loginWithFirebaseToken(tokenResult.token);

                    } catch (error) {
                        console.error(
                            "❌ AUTO VERIFICATION LOGIN ERROR:",
                            error
                        );

                        loginInProgressRef.current = false;

                        setError(
                            error.response?.data?.detail ||
                            error.message ||
                            "Automatic phone verification failed."
                        );

                        setIsLoading(false);
                    }
                }
            );

        verificationFailedListener =
            await FirebaseAuthentication.addListener(
                "phoneVerificationFailed",
                (event) => {
                    if (cancelled) return;

                    console.error(
                        "❌ Phone verification failed:",
                        event
                    );

                    clearOtpTimeout();

                    loginInProgressRef.current = false;

                    const message =
                        event?.message ||
                        "Unknown Firebase phone verification error";

                    setError(
                        getFirebaseErrorMessage(message)
                    );

                    setIsLoading(false);
                }
            );
    };

    setupFirebaseListeners();

    return () => {
        cancelled = true;

        clearOtpTimeout();

        codeSentListener?.remove();
        verificationCompletedListener?.remove();
        verificationFailedListener?.remove();
    };
}, [isNative]);
    // ── Web-only: reCAPTCHA verifier ──
    const getRecaptchaVerifier = () => {
        if (window.recaptchaVerifierLogin) {
            try { window.recaptchaVerifierLogin.clear(); } catch (e) {}
            window.recaptchaVerifierLogin = null;
        }
        window.recaptchaVerifierLogin = new RecaptchaVerifier(
            auth,
            "recaptcha-container-login",
            { size: "invisible" }
        );
        return window.recaptchaVerifierLogin;
    };
const loginWithFirebaseToken = async (firebaseToken) => {
    if (loginInProgressRef.current) {
        console.log(
            "⚠️ Login already in progress. Ignoring duplicate callback."
        );
        return;
    }

    loginInProgressRef.current = true;

    try {
        const activePhone = phoneRef.current;

        if (!activePhone || activePhone.length !== 10) {
            throw new Error(
                "Phone number is missing from the login session."
            );
        }

        console.log(
            "🔐 Logging into MealBee backend for:",
            activePhone
        );

        // Unified endpoint: logs in existing customers, or - for a brand
        // new phone number - tells us it needs a name before it can
        // finish creating the account (needs_name: true).
        const response = await api.post("/users/customer-auth-otp", {
            phone: activePhone,
            firebase_token: firebaseToken
        });

        if (response.data.needs_name) {
            console.log("👋 New phone number - showing agreement, then prompting for name.");
            setPendingFirebaseToken(firebaseToken);
            setNeedsName(true);
            setShowAgreement(true);
            loginInProgressRef.current = false;
            setIsLoading(false);
            return;
        }

        if (!response.data.access_token) {
            throw new Error(
                response.data.message || "Login failed"
            );
        }

        await completeLogin(
            response.data.access_token,
            response.data.role,
            response.data.user_id
        );

    } catch (error) {
        console.error(
            "❌ MEALBEE LOGIN ERROR:",
            error
        );

        loginInProgressRef.current = false;

        throw error;
    }
};

    // ── NEW: finish creating a brand-new customer's account once we
    // have their name, reusing the already-verified Firebase token ──
    const submitName = async () => {
        setError("");

        if (!fullName.trim()) {
            setError("Please enter your name");
            return;
        }

        try {
            setIsLoading(true);

            const activePhone = phoneRef.current;

            const response = await api.post("/users/customer-auth-otp", {
                phone: activePhone,
                firebase_token: pendingFirebaseToken,
                full_name: fullName.trim(),
                agreed_to_terms: agreedToTerms
            });

            if (!response.data.access_token) {
                throw new Error(
                    response.data.message || "Account creation failed"
                );
            }

            await completeLogin(
                response.data.access_token,
                response.data.role,
                response.data.user_id
            );

        } catch (error) {
            console.error("❌ NAME SUBMIT ERROR:", error);

            setError(
                error.response?.data?.detail ||
                error.message ||
                "Something went wrong. Please try again."
            );

        } finally {
            setIsLoading(false);
        }
    };

    // ── Shared: finish login once we have a token + role from backend ──
    const completeLogin = async (accessToken, role, userId) => {
        const isAdmin = role === "admin";
        if (!isNative && !isAdmin) {
            alert("This account must use the official MealBee Android App!");
            window.location.href = "/landing";
            return;
        }

        localStorage.setItem("token", accessToken);
        localStorage.setItem("role", role);
        localStorage.setItem("user_id", userId);

        // Restaurant owners & delivery partners have their accounts
        // created by admin, so there's no signup step to gate an
        // agreement behind - check on login instead, and hold the
        // dashboard redirect until they've accepted.
        if (role === "restaurant_owner" || role === "delivery_partner") {
            const agreementType = role === "restaurant_owner"
                ? "restaurant_agreement"
                : "delivery_agreement";

            try {
                const statusResponse = await api.get(
                    `/users/agreement-status?agreement_type=${agreementType}`,
                    {
                        headers: { Authorization: `Bearer ${accessToken}` },
                        // Fail open below instead of hanging forever - a
                        // cold-starting backend (e.g. Railway spinning up
                        // after idling) can otherwise freeze this request
                        // for 10-30s+ with no feedback to the user, which
                        // is exactly the "stuck on Verifying..." bug.
                        timeout: 15000
                    }
                );

                if (!statusResponse.data.has_agreed) {
                    setPendingRoleAgreement({ role, agreementType, accessToken, userId });
                    return; // modal takes over from here
                }
            } catch (error) {
                console.log("Agreement status check failed:", error);
                // Fail open - a check failure shouldn't lock a legitimate
                // partner out of their dashboard.
            }
        }

        setupPushNotifications(userId).catch((error) => {
            console.log("Push notification setup failed (non-blocking):", error);
        });

        redirectForRole(role);
    };

    const redirectForRole = (role) => {
        if (role === "admin") {
            window.location.href = "/admin-dashboard";
        } else if (role === "restaurant_owner") {
            window.location.href = "/restaurant-dashboard";
        } else if (role === "delivery_partner") {
            window.location.href = "/delivery-dashboard";
        } else if (role === "customer") {
    window.location.href = "/location-setup";
}
    };

    // ── Step 1: user enters phone, we check the account exists + role restriction ──
    const continueWithPhone = async () => {
        setError("");
        if (phone.length !== 10) {
            setError("Please enter a valid 10-digit phone number");
            return;
        }

        try {
            setIsLoading(true);
            const response = await api.post("/users/check-role", { phone });

            if (!response.data.exists) {
                // Brand-new phone number - customer self-signup only
                // happens through the native app. It's fine to let this
                // continue to OTP; the account gets created after
                // verification via /users/customer-auth-otp.
                if (!isNative) {
                    setError("This account must use the official MealBee Android App!");
                    setIsLoading(false);
                    return;
                }

                await sendOTP();
                return;
            }

            if (response.data.blocked) {
                setError("This account has been blocked by admin.");
                setIsLoading(false);
                return;
            }

            const role = response.data.role;
            const isAdmin = role === "admin";

            // Same restriction as before: only admin can use web browser
            if (!isNative && !isAdmin) {
                setError("This account must use the official MealBee Android App!");
                setIsLoading(false);
                return;
            }

            await sendOTP();

        } catch (err) {
            console.error(err);
            setError("Something went wrong. Please try again.");
            setIsLoading(false);
        }
    };

    // ── Send OTP: native plugin OR web SDK, depending on platform ──
   const sendOTP = async () => {
    const requestId = ++otpRequestIdRef.current;

    try {
        clearOtpTimeout();

        loginInProgressRef.current = false;

        setVerificationId("");
        setConfirmationResult(null);
        setOtp("");
        setOtpSent(false);
        setError("");

        const activePhone = phoneRef.current;
        const formattedPhone = `+91${activePhone}`;

        console.log(
            "📱 OTP request:",
            requestId,
            "| Phone:",
            formattedPhone,
            "| Native:",
            isNative
        );

        otpTimeoutRef.current = setTimeout(() => {
            if (requestId !== otpRequestIdRef.current) {
                return;
            }

            console.error(
                "❌ OTP request timed out:",
                requestId
            );

            setIsLoading(false);

            setError(
                "OTP request timed out. Please check your network and try again."
            );
        }, 30000);

        if (isNative) {
            await FirebaseAuthentication.signInWithPhoneNumber({
                phoneNumber: formattedPhone
            });

        } else {
            const verifier = getRecaptchaVerifier();

            const result = await signInWithPhoneNumber(
                auth,
                formattedPhone,
                verifier
            );

            if (requestId !== otpRequestIdRef.current) {
                return;
            }

            clearOtpTimeout();

            setConfirmationResult(result);
setOtpSent(true);
setResendTimer(30);
setIsLoading(false);
        }

    } catch (error) {
        console.error("❌ SEND OTP ERROR:", error);

        clearOtpTimeout();

        loginInProgressRef.current = false;

        setError(
            getFirebaseErrorMessage(error)
        );

        setIsLoading(false);

        if (window.recaptchaVerifierLogin) {
            try {
                window.recaptchaVerifierLogin.clear();
            } catch (e) {}

            window.recaptchaVerifierLogin = null;
        }
    }
};
const resendOTP = async () => {
    if (resendTimer > 0 || isResending) {
        return;
    }

    setIsResending(true);
    setError("");

    try {
        await sendOTP();

    } catch (error) {
        console.error("❌ RESEND OTP ERROR:", error);

        setError(
            getFirebaseErrorMessage(error)
        );

    } finally {
        setIsResending(false);
    }
};
    // ── Verify OTP then login (native OR web) ──
    const verifyOtpAndLogin = async () => {
        setError("");
        if (otp.length !== 6) {
            setError("Please enter the 6-digit OTP");
            return;
        }

        try {
            setIsLoading(true);
            let firebaseToken = "";

            if (isNative) {
    if (!verificationId) {
        setError(
            "OTP session is missing. Please request a new OTP."
        );

        setIsLoading(false);
        return;
    }

    console.log(
        "🔐 Confirming OTP using verification ID:",
        verificationId
    );

    await FirebaseAuthentication.confirmVerificationCode({
        verificationId,
        verificationCode: otp.trim()
    });

    const tokenResult =
        await FirebaseAuthentication.getIdToken({
            forceRefresh: true
        });

    if (!tokenResult?.token) {
        throw new Error(
            "Firebase ID token was not returned."
        );
    }

    firebaseToken = tokenResult.token;
} else {
                if (!confirmationResult) {
                    setError("Session expired. Please request OTP again.");
                    setIsLoading(false);
                    return;
                }
                const result = await confirmationResult.confirm(otp.trim());
                firebaseToken = await result.user.getIdToken();
            }

            await loginWithFirebaseToken(firebaseToken);

        } catch (error) {
    console.error("❌ OTP LOGIN ERROR:", error);

    loginInProgressRef.current = false;

    setError(
        error.response?.data?.detail ||
        getFirebaseErrorMessage(error)
    );

} finally {
    setIsLoading(false);
}
    };

 const goBackToPhone = () => {
    otpRequestIdRef.current += 1;

    clearOtpTimeout();

    loginInProgressRef.current = false;

    setOtpSent(false);
    setOtp("");
    setVerificationId("");
    setConfirmationResult(null);
    setResendTimer(0);
    setError("");
    setIsLoading(false);

    // Reset the new-user name step too, in case they back out mid-signup
    setNeedsName(false);
    setFullName("");
    setPendingFirebaseToken("");
    setShowAgreement(false);
    setAgreedToTerms(false);
};
    return (
        <>
            <style>{css}</style>

            {/* Web-only invisible reCAPTCHA container */}
            {!isNative && <div id="recaptcha-container-login" ref={recaptchaContainerRef}></div>}

            <div className="app-layout">
                <div className="app-container">

                    {/* True 3D Front-View Orbit */}
                    <div className="orbit-container">
                        <div className="scene">
                            <div className="center-logo">MealBee</div>
                            <div className="orbit-pivot">
                                <div className="orbit-arm" style={{ transform: 'rotateY(0deg)' }}>
                                    <div className="orbit-distance">
                                        <div className="orbit-counter">
                                            <div className="orbit-item" style={{ transform: 'rotateY(0deg)' }}>🍔</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="orbit-arm" style={{ transform: 'rotateY(90deg)' }}>
                                    <div className="orbit-distance">
                                        <div className="orbit-counter">
                                            <div className="orbit-item" style={{ transform: 'rotateY(-90deg)' }}>🍕</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="orbit-arm" style={{ transform: 'rotateY(180deg)' }}>
                                    <div className="orbit-distance">
                                        <div className="orbit-counter">
                                            <div className="orbit-item" style={{ transform: 'rotateY(-180deg)' }}>🍟</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="orbit-arm" style={{ transform: 'rotateY(270deg)' }}>
                                    <div className="orbit-distance">
                                        <div className="orbit-counter">
                                            <div className="orbit-item" style={{ transform: 'rotateY(-270deg)' }}>🥤</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Inputs */}
                    <div className="form-section">
                        <div className="fade-in-up">
                            <h2 className="title-text">
                                {needsName ? "Welcome to MealBee!" : "Welcome Back"}
                            </h2>
                            <p className="subtitle-text">
                                {!otpSent && !needsName && "Enter your phone number to continue."}
                                {otpSent && !needsName && `OTP sent to +91 ${phone}`}
                                {needsName && "You're verified! What should we call you?"}
                            </p>
                        </div>

                        {error && <div className="error-box fade-in-up">❌ {error}</div>}

                        {/* ── STEP 1: Phone Number ── */}
                        {!otpSent && !needsName && (
                            <>
                                <div className="input-stack fade-in-up delay-1">
                                    <div className="phone-input-wrapper single-input">
                                        <span className="phone-prefix">+91</span>
                                        <input
                                            type="tel"
                                            className="app-input phone-input"
                                            placeholder="Phone Number"
                                            maxLength="10"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <div className="fade-in-up delay-2">
                                    <button className="btn-primary" onClick={continueWithPhone} disabled={isLoading}>
                                        {isLoading ? "Please wait..." : "Continue"}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* ── STEP 2: OTP entry ── */}
                        {otpSent && !needsName && (
                            <>
                                <div className="input-stack fade-in-up delay-1">
                                    <input
                                        type="text"
                                        className="app-input input-only otp-input"
                                        placeholder="000000"
                                        maxLength="6"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="fade-in-up delay-2">
                                    <button className="btn-primary" onClick={verifyOtpAndLogin} disabled={isLoading || otp.length !== 6}>
                                        {isLoading ? "Verifying..." : "Verify & Login"}
                                    </button>
                                </div>
<div className="resend-section fade-in-up delay-3">
    <span className="resend-label">
        Didn't receive the OTP?
    </span>

    {resendTimer > 0 ? (
        <span className="resend-timer">
            Resend OTP in {resendTimer}s
        </span>
    ) : (
        <button
            className="resend-button"
            onClick={resendOTP}
            disabled={isResending}
        >
            {isResending ? "Sending..." : "Resend OTP"}
        </button>
    )}
</div>
                                <button className="btn-text fade-in-up delay-3" onClick={goBackToPhone}>
                                    ← Use a different number
                                </button>
                            </>
                        )}

                        {/* ── STEP 3: NEW - first name for a brand-new account ── */}
                        {needsName && !showAgreement && (
                            <>
                                <div className="input-stack fade-in-up delay-1">
                                    <input
                                        type="text"
                                        className="app-input input-only"
                                        placeholder="Your full name"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="fade-in-up delay-2">
                                    <button className="btn-primary" onClick={submitName} disabled={isLoading || !fullName.trim()}>
                                        {isLoading ? "Creating account..." : "Continue"}
                                    </button>
                                </div>

                                <button className="btn-text fade-in-up delay-3" onClick={goBackToPhone}>
                                    ← Use a different number
                                </button>
                            </>
                        )}

                    </div>
                </div>
            </div>

            {/* ── NEW: consent popup, shown once per brand-new account before it's created ── */}
            {showAgreement && (
    <AgreementModal
        agreementType="customer_agreement"
        onAccept={() => {
            setAgreedToTerms(true);
            setShowAgreement(false);
        }}
        onDecline={() => {
            setError("You need to agree to the Terms & Conditions and Privacy Policy to create an account.");
            goBackToPhone();
        }}
    />
)}

            {/* ── NEW: restaurant/delivery agreement, shown on first login
                 since these accounts are created by admin, not self-signup ── */}
            {pendingRoleAgreement && (
                <AgreementModal
                    title={
                        pendingRoleAgreement.role === "restaurant_owner"
                            ? "Restaurant Partner Agreement"
                            : "Delivery Partner Agreement"
                    }
                    summary="Before you can access your dashboard, please review and accept your partner agreement with MealBee."
                    links={[{
                        label: pendingRoleAgreement.role === "restaurant_owner"
                            ? "Restaurant Partner Agreement"
                            : "Delivery Partner Agreement",
                        url: pendingRoleAgreement.role === "restaurant_owner"
                            ? "/restaurant-agreement"
                            : "/delivery-agreement"
                    }]}
                    onAgree={async () => {
                        try {
                            await api.post(
                                "/users/accept-agreement",
                                { agreement_type: pendingRoleAgreement.agreementType },
                                { headers: { Authorization: `Bearer ${pendingRoleAgreement.accessToken}` } }
                            );

                            // Same fix as completeLogin - don't let a stuck
                            // permission-dialog promise block the redirect.
                            setupPushNotifications(pendingRoleAgreement.userId).catch((error) => {
                                console.log("Push notification setup failed (non-blocking):", error);
                            });

                            redirectForRole(pendingRoleAgreement.role);
                        } catch (error) {
                            console.error("❌ AGREEMENT ACCEPT ERROR:", error);
                            setError("Couldn't record your agreement. Please try again.");
                        }
                    }}
                    onDecline={() => {
                        // Don't leave a half-authenticated session behind
                        localStorage.removeItem("token");
                        localStorage.removeItem("role");
                        localStorage.removeItem("user_id");
                        setPendingRoleAgreement(null);
                        setError("You must accept the agreement to continue using your account.");
                    }}
                />
            )}
        </>
    );
}

const css = `
    html, body { margin: 0; padding: 0; background-color: #f0f2f5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
    .app-layout { display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; background-color: #f0f2f5; }
    .app-container { width: 100%; max-width: 480px; min-height: 80vh; background-color: #ffffff; display: flex; flex-direction: column; border-radius: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); overflow: hidden; position: relative; }

    @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    .fade-in-up { animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }

    .orbit-container { position: relative; width: 100%; height: 260px; background: linear-gradient(135deg, #fff3e6 0%, #ffffff 100%); border-bottom: 1px solid rgba(0,0,0,0.05); }
    .scene { position: relative; width: 100%; height: 100%; perspective: 800px; display: flex; justify-content: center; align-items: center; transform-style: preserve-3d; }
    .center-logo { position: absolute; z-index: 10; font-size: 36px; font-weight: 900; color: #ff6b00; letter-spacing: -1px; background: rgba(255, 255, 255, 0.95); padding: 10px 24px; border-radius: 30px; box-shadow: 0 8px 25px rgba(255,107,0,0.15); transform: translateZ(1px); }
    .orbit-pivot { position: absolute; width: 0; height: 0; transform-style: preserve-3d; animation: spinY 12s linear infinite; }
    @keyframes spinY { from { transform: rotateX(-12deg) rotateY(0deg); } to { transform: rotateX(-12deg) rotateY(360deg); } }
    .orbit-arm { position: absolute; top: 0; left: 0; transform-style: preserve-3d; }
    .orbit-distance { position: absolute; transform: translateZ(140px); transform-style: preserve-3d; }
    .orbit-counter { position: absolute; transform-style: preserve-3d; animation: counterSpinY 12s linear infinite; }
    @keyframes counterSpinY { from { transform: rotateY(0deg) rotateX(12deg); } to { transform: rotateY(-360deg) rotateX(12deg); } }
    .orbit-item { position: absolute; left: -20px; top: -20px; width: 40px; height: 40px; display: flex; justify-content: center; align-items: center; font-size: 34px; filter: drop-shadow(0 5px 8px rgba(0,0,0,0.2)); }

    .form-section { padding: 30px 24px; flex-grow: 1; display: flex; flex-direction: column; }
    .title-text { font-size: 24px; font-weight: 800; color: #1c1c1c; margin: 0 0 5px 0; }
    .subtitle-text { font-size: 14px; color: #7e7e7e; margin: 0 0 20px 0; }

    .error-box { background: #ffe6e6; border: 1px solid #ffcccc; border-radius: 12px; padding: 12px 14px; color: #c41e1e; font-size: 13px; margin-bottom: 16px; }

    .input-stack { display: flex; flex-direction: column; margin-bottom: 24px; }
    .app-input { padding: 16px; font-size: 15px; border: 1px solid #e2e8f0; background: #f8fafc; outline: none; color: #1c1c1c; transition: all 0.2s; font-family: inherit; font-weight: 500; box-sizing: border-box; width: 100%; }
    .input-only { border-radius: 12px; }
    .app-input:focus { border-color: #ff6b00; background: #ffffff; position: relative; z-index: 2; box-shadow: 0 0 0 3px rgba(255,107,0,0.1); }
    .app-input::placeholder { color: #a0aec0; font-weight: 400; }
    .otp-input { font-size: 20px; letter-spacing: 6px; text-align: center; font-weight: 700; }

    .phone-input-wrapper { display: flex; align-items: center; border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 12px; overflow: hidden; }
    .phone-input-wrapper:focus-within { border-color: #ff6b00; background: #ffffff; box-shadow: 0 0 0 3px rgba(255,107,0,0.1); }
    .phone-prefix { padding: 16px 0 16px 16px; font-size: 15px; font-weight: 700; color: #686b78; }
    .phone-input-wrapper .phone-input { border: none; background: transparent; box-shadow: none; }

    .btn-primary { background: #ff6b00; color: #fff; border: none; padding: 16px; font-size: 16px; font-weight: 800; border-radius: 12px; width: 100%; cursor: pointer; text-transform: uppercase; letter-spacing: 0.5px; box-shadow: 0 4px 15px rgba(255, 107, 0, 0.25); transition: all 0.2s; }
    .btn-primary:disabled { background: #fcae5a; cursor: not-allowed; box-shadow: none; }
    .btn-primary:active:not(:disabled) { transform: scale(0.98); box-shadow: 0 2px 10px rgba(255, 107, 0, 0.15); }

    .btn-outline { background: #ffffff; color: #ff6b00; border: 1px solid #ff6b00; padding: 16px; font-size: 16px; font-weight: 800; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; }
    .btn-outline:active { transform: scale(0.98); }

    .btn-text { background: none; border: none; color: #718096; font-size: 14px; font-weight: 600; cursor: pointer; width: 100%; padding: 15px; margin-top: 5px; transition: color 0.2s; text-align: center; }
    .btn-text:hover { color: #1c1c1c; }

    .divider { display: flex; align-items: center; text-align: center; margin: 20px 0; color: #a0aec0; font-size: 12px; font-weight: 700; text-transform: uppercase; }
    .divider::before, .divider::after { content: ''; flex: 1; border-bottom: 1px solid #edf2f7; }
    .divider:not(:empty)::before { margin-right: 15px; }
    .divider:not(:empty)::after { margin-left: 15px; }
.resend-section {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    margin-top: 18px;
    font-size: 14px;
}

.resend-label {
    color: #718096;
}

.resend-timer {
    color: #718096;
    font-weight: 600;
}

.resend-button {
    background: none;
    border: none;
    color: #ff6b00;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
}

.resend-button:disabled {
    color: #a0aec0;
    cursor: not-allowed;
}
    @media (max-width: 480px) {
        .app-layout { padding: 0; align-items: flex-start; }
        .app-container { border-radius: 0; box-shadow: none; min-height: 100vh; }
    }
`;

export default Login;